import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';
import { isLscYoutubeOauthEnabled } from '../../config/featureFlags';
import {
  YOUTUBE_AUTH_ERROR_MESSAGES,
  buildYouTubeConnectUrl,
} from '../../utils/authUrls';
import { disconnectYouTube, getYouTubeConnection } from '../api';
import type { LscYouTubeConnection } from '../api';

interface YouTubeIdentityPanelProps {
  returnTo: string;
}

const YouTubeIdentityPanel: React.FC<YouTubeIdentityPanelProps> = ({ returnTo }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [connection, setConnection] = useState<LscYouTubeConnection | null>(null);
  const [loaded, setLoaded] = useState(false);

  const authError = searchParams.get('auth_error');
  const connectedSuccess = searchParams.get('youtube_connected') === '1';

  const authErrorMessage = useMemo(() => {
    if (!authError) return null;
    return (
      YOUTUBE_AUTH_ERROR_MESSAGES[authError] ||
      'YouTube connection failed. Please try again.'
    );
  }, [authError]);

  const refreshConnection = async () => {
    try {
      const next = await getYouTubeConnection();
      setConnection(next);
    } catch {
      setConnection(null);
    } finally {
      setLoaded(true);
    }
  };

  useEffect(() => {
    if (!isLscYoutubeOauthEnabled()) return;
    void refreshConnection();
  }, []);

  useEffect(() => {
    if (!connectedSuccess && !authError) return;
    if (connectedSuccess) {
      void refreshConnection();
    }
    const next = new URLSearchParams(searchParams);
    next.delete('youtube_connected');
    next.delete('auth_error');
    setSearchParams(next, { replace: true });
  }, [authError, connectedSuccess, searchParams, setSearchParams]);

  if (!isLscYoutubeOauthEnabled()) {
    return null;
  }

  const handleConnect = () => {
    window.location.href = buildYouTubeConnectUrl(returnTo);
  };

  const handleDisconnect = async () => {
    setLoading(true);
    setError(null);
    try {
      await disconnectYouTube();
      await refreshConnection();
    } catch {
      setError('Could not disconnect YouTube. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const status = connection?.connection_status || 'disconnected';
  const connected = Boolean(connection?.connected) && status !== 'disconnected';
  const needsReauth = status === 'needs_reauth';
  const title = connection?.youtube_channel_title || connection?.youtube_channel_id;

  return (
    <div className="card space-y-4" data-testid="youtube-identity-panel">
      <div>
        <h2 className="font-semibold text-white">YouTube channel</h2>
        <p className="text-sm text-secondary-400 mt-1">
          Connect YouTube to enable permanent archive sync for your channel. Tokens stay
          encrypted on the server and never appear in this UI.
        </p>
      </div>

      {connectedSuccess && (
        <p className="text-sm text-green-300" data-testid="youtube-connected-success">
          YouTube connected successfully.
        </p>
      )}
      {(authErrorMessage || error) && (
        <p className="text-sm text-red-300" data-testid="youtube-connect-error">
          {error || authErrorMessage}
        </p>
      )}

      {!loaded ? (
        <p className="text-sm text-secondary-500">Checking YouTube connection…</p>
      ) : needsReauth ? (
        <div className="flex items-center gap-3" data-testid="youtube-needs-reauth">
          <div className="flex-1 min-w-0">
            <p className="text-white font-medium truncate">
              {title || 'YouTube channel'}
            </p>
            <p className="text-sm text-amber-300">Reconnect required</p>
          </div>
          <button
            type="button"
            className="btn-primary"
            onClick={handleConnect}
            data-testid="reconnect-youtube-button"
          >
            Reconnect
          </button>
        </div>
      ) : connected ? (
        <div className="flex items-center gap-3" data-testid="youtube-connected-profile">
          <div className="flex-1 min-w-0">
            <p className="text-white font-medium truncate">{title || 'YouTube channel'}</p>
            <p className="text-sm text-green-300">Connected ✓</p>
          </div>
          <button
            type="button"
            className="btn-secondary"
            onClick={handleDisconnect}
            disabled={loading}
            data-testid="disconnect-youtube-button"
          >
            {loading ? 'Disconnecting...' : 'Disconnect'}
          </button>
        </div>
      ) : (
        <button
          type="button"
          className="btn-primary"
          onClick={handleConnect}
          data-testid="connect-youtube-button"
        >
          Connect YouTube
        </button>
      )}
    </div>
  );
};

export default YouTubeIdentityPanel;
